import { Button } from '@/client/components/ui/button';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import { PRIVACY_LINK, RISK_DISCLOSURE_LINK, TERMS_OF_USE_LINK } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { getAuthToken } from '@/client/store/auth-token-store';
import { api } from '@/client/utils/api';
import { recordTermsAccepted, TERMS_VERSION } from '@/client/utils/terms';
import { useMe } from '@/libs/react-sdk/api/use-me';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

/**
 * One-time terms + risk acknowledgment after first sign-in (regulatory ask).
 * Pattern: US DeFi venues show an implicit consent line on connect (we do too,
 * via the AppKit legal footer); a venue positioned as regulated additionally
 * takes an explicit first-login acknowledgment with a risk disclosure.
 *
 * Source of truth is the server record (/me.termsVersion, written by
 * POST /auth/accept-terms). If the server write fails, the gate closes on the
 * local record and simply re-prompts next session — self-healing, never walls
 * the user on a network hiccup.
 * Blocking: no outside-dismiss, no escape — Accept continues, Decline signs out.
 */
export const TermsGate = () => {
  const { t } = useTranslation();
  const { isAuthed, data, logout } = useAuth();
  const uid = data?.uid;
  const me = useMe(api, { enabled: !!uid && isAuthed });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [docsOk, setDocsOk] = useState(false);
  const [riskOk, setRiskOk] = useState(false);
  const [location] = useLocation();

  const open = !!uid && isAuthed && me.isSuccess && me.data?.termsVersion !== TERMS_VERSION && !accepted;
  // Never cover the reading surface: the gate links its documents to /pdf in a
  // new tab, where this SAME gate would otherwise render on top of the text
  // the user is being asked to read before accepting.
  if (location.startsWith('/pdf')) return null;
  if (!open) return null;

  const onAccept = async () => {
    if (!docsOk || !riskOk || !uid || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/accept-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ version: TERMS_VERSION }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.code !== 0) throw new Error(body.msg || 'accept-terms failed');
      me.refetch();
    } catch {
      // Server record failed: close on the local record; the server-truth gate
      // re-prompts next session and retries the write.
    } finally {
      setSubmitting(false);
    }
    recordTermsAccepted(uid);
    setAccepted(true);
  };

  const onDecline = async () => {
    setDocsOk(false);
    setRiskOk(false);
    await logout();
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('terms-gate-title', 'Before you trade')}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
          {t(
            'terms-gate-risk',
            'Prediction markets are risky. Prices move with the crowd and can go to zero, and settlement is final once an outcome resolves on-chain. Only commit funds you can afford to lose. Nothing on SakerX is investment advice.'
          )}
        </DialogDescription>
        <div className="flex gap-3">
          <a
            href={TERMS_OF_USE_LINK}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-foreground/80 hover:text-foreground transition-colors"
          >
            {t('terms-of-use', 'Terms of Use')}
          </a>
          <a
            href={PRIVACY_LINK}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-foreground/80 hover:text-foreground transition-colors"
          >
            {t('privacy-policy', 'Privacy Policy')}
          </a>
          <a
            href={RISK_DISCLOSURE_LINK}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-foreground/80 hover:text-foreground transition-colors"
          >
            {t('risk-disclosure', 'Risk Disclosure')}
          </a>
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={docsOk} onCheckedChange={v => setDocsOk(v === true)} className="mt-0.5" />
          <span className="text-sm leading-snug">
            {t('terms-gate-accept-docs', 'I have read and accept the Terms of Use and Privacy Policy.')}
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={riskOk} onCheckedChange={v => setRiskOk(v === true)} className="mt-0.5" />
          <span className="text-sm leading-snug">
            {t('terms-gate-accept-risk', 'I understand the risks, including the possible loss of all committed funds.')}
          </span>
        </label>
        <div className="flex flex-col gap-2 pt-1">
          <Button type="button" disabled={!docsOk || !riskOk || submitting} onClick={onAccept} className="w-full btn-brand">
            {t('terms-gate-continue', 'Accept and continue')}
          </Button>
          <Button type="button" variant="ghost" onClick={onDecline} className="w-full text-muted-foreground">
            {t('terms-gate-decline', 'Decline and sign out')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
